import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Goal, GoalResponse } from "../types";
import { getGoals, getResponsesForDate, saveResponse } from "../repository/daysRepository";

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isDueToday(goal: Goal, dateStr: string): boolean {
  if (goal.frequency_days === 1) return true;
  if (!goal.start_date) return true;
  const start = new Date(goal.start_date + "T00:00:00");
  const current = new Date(dateStr + "T00:00:00");
  const diffDays = Math.floor((current.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays % goal.frequency_days === 0;
}

interface ResponseDraft {
  value: boolean | number;
  notes: string;
  dirty: boolean;
}

const DailyQuestionnaire: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");

  const [goals, setGoals] = useState<Goal[]>([]);
  const [responses, setResponses] = useState<Map<string, GoalResponse>>(new Map());
  const [drafts, setDrafts] = useState<Map<string, ResponseDraft>>(new Map());
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [saveStatus, setSaveStatus] = useState<Map<string, "saved" | "error">>(new Map());
  const [date, setDate] = useState(dateParam || formatLocalDate(new Date()));
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const savedTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [goalsData, responsesData] = await Promise.all([
        getGoals(),
        getResponsesForDate(date),
      ]);

      const activeGoals = goalsData.filter((g) => g.status === "active");
      setGoals(activeGoals);

      const responseMap = new Map<string, GoalResponse>();
      responsesData.forEach((r) => responseMap.set(r.goal_uuid, r));
      setResponses(responseMap);

      const draftMap = new Map<string, ResponseDraft>();
      activeGoals.forEach((g) => {
        const existing = responseMap.get(g.uuid);
        draftMap.set(g.uuid, {
          value: existing ? existing.value : (g.goal_type === "binary" ? false : 0),
          notes: existing?.notes || "",
          dirty: false,
        });
      });
      setDrafts(draftMap);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateDraft = (goalUuid: string, field: "value" | "notes", val: boolean | number | string) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      const draft = next.get(goalUuid);
      if (draft) {
        next.set(goalUuid, { ...draft, [field]: val, dirty: true });
      }
      return next;
    });
  };

  const autoSave = useCallback(async (goalUuid: string, value: boolean | number, notes: string) => {
    setSaving((prev) => new Set(prev).add(goalUuid));
    setSaveStatus((prev) => { const next = new Map(prev); next.delete(goalUuid); return next; });
    try {
      const saved = await saveResponse(goalUuid, date, value, notes);
      setResponses((prev) => {
        const next = new Map(prev);
        next.set(goalUuid, saved);
        return next;
      });
      setDrafts((prev) => {
        const next = new Map(prev);
        const draft = next.get(goalUuid);
        if (draft) next.set(goalUuid, { ...draft, dirty: false });
        return next;
      });
      setSaveStatus((prev) => { const next = new Map(prev); next.set(goalUuid, "saved"); return next; });
      const existing = savedTimers.current.get(goalUuid);
      if (existing) clearTimeout(existing);
      savedTimers.current.set(goalUuid, setTimeout(() => {
        setSaveStatus((prev) => {
          const next = new Map(prev);
          if (next.get(goalUuid) === "saved") next.delete(goalUuid);
          return next;
        });
        savedTimers.current.delete(goalUuid);
      }, 2000));
    } catch (err) {
      console.error("Failed to save:", err);
      setSaveStatus((prev) => { const next = new Map(prev); next.set(goalUuid, "error"); return next; });
    } finally {
      setSaving((prev) => { const next = new Set(prev); next.delete(goalUuid); return next; });
    }
  }, [date]);

  const scheduleSave = useCallback((goalUuid: string, value: boolean | number, notes: string) => {
    const existing = debounceTimers.current.get(goalUuid);
    if (existing) clearTimeout(existing);
    debounceTimers.current.set(goalUuid, setTimeout(() => {
      autoSave(goalUuid, value, notes);
      debounceTimers.current.delete(goalUuid);
    }, 1500));
  }, [autoSave]);

  // Clean up timers on unmount and date change
  useEffect(() => {
    return () => {
      debounceTimers.current.forEach((t) => clearTimeout(t));
      debounceTimers.current.clear();
      savedTimers.current.forEach((t) => clearTimeout(t));
      savedTimers.current.clear();
    };
  }, [date]);

  const handleSave = async (goalUuid: string) => {
    // Flush any pending debounce for this goal
    const pending = debounceTimers.current.get(goalUuid);
    if (pending) {
      clearTimeout(pending);
      debounceTimers.current.delete(goalUuid);
    }
    const draft = drafts.get(goalUuid);
    if (!draft) return;
    await autoSave(goalUuid, draft.value, draft.notes);
  };

  const toggleNotes = (goalUuid: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(goalUuid)) next.delete(goalUuid);
      else next.add(goalUuid);
      return next;
    });
  };

  const goToDate = (offset: number) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + offset);
    setDate(formatLocalDate(d));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const isToday = date === formatLocalDate(new Date());

  return (
    <div className="questionnaire">
      <div className="date-nav">
        <button className="btn-icon" onClick={() => goToDate(-1)}>&larr;</button>
        <h2 className="date-title">
          {isToday ? "Today" : new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          <span className="date-subtitle">{date}</span>
        </h2>
        <button className="btn-icon" onClick={() => goToDate(1)}>&rarr;</button>
      </div>

      {goals.length === 0 && (
        <div className="empty-state">
          <p>No active goals yet.</p>
          <a href="/goals" className="link">Create your first goal</a>
        </div>
      )}

      <div className="goal-cards">
        {goals.map((goal) => {
          const draft = drafts.get(goal.uuid);
          const due = isDueToday(goal, date);
          const isSaving = saving.has(goal.uuid);
          const hasExisting = responses.has(goal.uuid);
          const status = saveStatus.get(goal.uuid);

          return (
            <div key={goal.uuid} className={`goal-card ${!due ? "not-due" : ""} ${hasExisting && !draft?.dirty ? "saved" : ""}`}>
              <div className="goal-card-header">
                <div className="goal-card-title">
                  <h3>{goal.title}</h3>
                  {!due && <span className="due-badge">not due today</span>}
                  {goal.frequency_days > 1 && <span className="freq-badge">every {goal.frequency_days}d</span>}
                </div>
                {goal.goal_type === "numeric" && goal.target && (
                  <span className="target-badge">target: {goal.target}</span>
                )}
              </div>

              <div className="goal-card-input">
                {goal.goal_type === "binary" ? (
                  <div className="binary-toggle">
                    <button
                      type="button"
                      className={`binary-toggle-btn ${draft?.value !== true ? "active" : ""}`}
                      onClick={() => {
                        updateDraft(goal.uuid, "value", false);
                        autoSave(goal.uuid, false, draft?.notes || "");
                      }}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      className={`binary-toggle-btn ${draft?.value === true ? "active" : ""}`}
                      onClick={() => {
                        updateDraft(goal.uuid, "value", true);
                        autoSave(goal.uuid, true, draft?.notes || "");
                      }}
                    >
                      Yes
                    </button>
                  </div>
                ) : (
                  <input
                    type="number"
                    className="number-input"
                    value={draft?.value as number ?? 0}
                    onChange={(e) => {
                      const newValue = parseFloat(e.target.value) || 0;
                      updateDraft(goal.uuid, "value", newValue);
                      scheduleSave(goal.uuid, newValue, draft?.notes || "");
                    }}
                    min={0}
                    step="any"
                  />
                )}

                <div className="goal-card-actions">
                  <button
                    className="btn-notes"
                    onClick={() => toggleNotes(goal.uuid)}
                    title="Add notes"
                  >
                    {expandedNotes.has(goal.uuid) ? "Hide notes" : "Notes"}
                  </button>
                  {isSaving ? (
                    <span className="save-status saving">Saving...</span>
                  ) : status === "saved" ? (
                    <span className="save-status saved">Saved</span>
                  ) : status === "error" ? (
                    <button className="btn-save error" onClick={() => handleSave(goal.uuid)}>
                      Retry
                    </button>
                  ) : draft?.dirty ? (
                    <button
                      className="btn-save"
                      onClick={() => handleSave(goal.uuid)}
                    >
                      Save
                    </button>
                  ) : hasExisting ? (
                    <span className="save-status saved-idle">Saved</span>
                  ) : null}
                </div>
              </div>

              {expandedNotes.has(goal.uuid) && (
                <textarea
                  className="notes-input"
                  placeholder="Add notes..."
                  value={draft?.notes || ""}
                  onChange={(e) => {
                    const newNotes = e.target.value;
                    updateDraft(goal.uuid, "notes", newNotes);
                    scheduleSave(goal.uuid, draft?.value ?? (goal.goal_type === "binary" ? false : 0), newNotes);
                  }}
                  rows={2}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyQuestionnaire;
